import type { DecodedVideoFrame } from "./messages";

export function createYuvRenderer(canvas: HTMLCanvasElement) {
  const gl = canvas.getContext("webgl", { alpha: false, antialias: false, depth: false });
  if (!gl) throw new Error("This browser could not display the video.");
  const shader = (type: number, source: string) => {
    const compiled = gl.createShader(type);
    if (!compiled) throw new Error("The video renderer could not be created.");
    gl.shaderSource(compiled, source);
    gl.compileShader(compiled);
    if (!gl.getShaderParameter(compiled, gl.COMPILE_STATUS)) {
      const message = gl.getShaderInfoLog(compiled);
      gl.deleteShader(compiled);
      throw new Error(message ?? "The video renderer could not be created.");
    }
    return compiled;
  };
  const vertex = shader(
    gl.VERTEX_SHADER,
    `
    attribute vec2 position;
    varying vec2 coordinate;
    void main() {
      coordinate = vec2((position.x + 1.0) * 0.5, (1.0 - position.y) * 0.5);
      gl_Position = vec4(position, 0.0, 1.0);
    }
  `,
  );
  const fragment = shader(
    gl.FRAGMENT_SHADER,
    `
    precision mediump float;
    varying vec2 coordinate;
    uniform sampler2D luma;
    uniform sampler2D chromaU;
    uniform sampler2D chromaV;
    uniform vec3 offset;
    uniform mat3 conversion;
    uniform vec3 lumaCoordinates;
    uniform vec3 chromaCoordinates;
    void main() {
      vec2 lumaCoordinate = vec2(clamp(coordinate.x * lumaCoordinates.x,
        lumaCoordinates.y, lumaCoordinates.z), coordinate.y);
      vec2 chromaCoordinate = vec2(clamp(coordinate.x * chromaCoordinates.x,
        chromaCoordinates.y, chromaCoordinates.z), coordinate.y);
      vec3 yuv = vec3(texture2D(luma, lumaCoordinate).r,
        texture2D(chromaU, chromaCoordinate).r, texture2D(chromaV, chromaCoordinate).r);
      gl_FragColor = vec4(conversion * (yuv - offset), 1.0);
    }
  `,
  );
  const program = gl.createProgram();
  if (!program) throw new Error("The video renderer could not be created.");
  gl.attachShader(program, vertex);
  gl.attachShader(program, fragment);
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    throw new Error("The video renderer could not be created.");
  }
  // biome-ignore lint/correctness/useHookAtTopLevel: useProgram is a WebGL method, not a React hook
  gl.useProgram(program);
  const vertices = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, vertices);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
  const position = gl.getAttribLocation(program, "position");
  gl.enableVertexAttribArray(position);
  gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
  gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
  const textures = ["luma", "chromaU", "chromaV"].map((name, index) => {
    const texture = gl.createTexture();
    gl.activeTexture(gl.TEXTURE0 + index);
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.uniform1i(gl.getUniformLocation(program, name), index);
    return texture;
  });
  const offset = gl.getUniformLocation(program, "offset");
  const conversion = gl.getUniformLocation(program, "conversion");
  const lumaCoordinates = gl.getUniformLocation(program, "lumaCoordinates");
  const chromaCoordinates = gl.getUniformLocation(program, "chromaCoordinates");
  let width = 0;
  let height = 0;
  let lumaStride = 0;
  let chromaStride = 0;
  let colorMatrix = -1;
  let fullRange: boolean | undefined;

  return {
    draw(frame: DecodedVideoFrame) {
      const resized =
        width !== frame.width ||
        height !== frame.height ||
        lumaStride !== frame.lumaStride ||
        chromaStride !== frame.chromaStride;
      if (resized) {
        width = frame.width;
        height = frame.height;
        lumaStride = frame.lumaStride;
        chromaStride = frame.chromaStride;
        canvas.width = width;
        canvas.height = height;
        gl.viewport(0, 0, width, height);
        gl.uniform3f(
          lumaCoordinates,
          width / lumaStride,
          0.5 / lumaStride,
          (width - 0.5) / lumaStride,
        );
        gl.uniform3f(
          chromaCoordinates,
          Math.ceil(width / 2) / chromaStride,
          0.5 / chromaStride,
          (Math.ceil(width / 2) - 0.5) / chromaStride,
        );
      }
      const chromaHeight = Math.ceil(height / 2);
      let start = 0;
      for (let plane = 0; plane < 3; plane++) {
        const planeWidth = plane ? chromaStride : lumaStride;
        const planeHeight = plane ? chromaHeight : height;
        const end = start + planeWidth * planeHeight;
        gl.activeTexture(gl.TEXTURE0 + plane);
        gl.bindTexture(gl.TEXTURE_2D, textures[plane]);
        const pixels = frame.pixels.subarray(start, end);
        if (resized) {
          gl.texImage2D(
            gl.TEXTURE_2D,
            0,
            gl.LUMINANCE,
            planeWidth,
            planeHeight,
            0,
            gl.LUMINANCE,
            gl.UNSIGNED_BYTE,
            pixels,
          );
        } else {
          gl.texSubImage2D(
            gl.TEXTURE_2D,
            0,
            0,
            0,
            planeWidth,
            planeHeight,
            gl.LUMINANCE,
            gl.UNSIGNED_BYTE,
            pixels,
          );
        }
        start = end;
      }
      if (colorMatrix !== frame.matrix || fullRange !== frame.fullRange) {
        colorMatrix = frame.matrix;
        fullRange = frame.fullRange;
        const [red, blue] =
          frame.matrix === 1
            ? [0.2126, 0.0722]
            : frame.matrix === 9
              ? [0.2627, 0.0593]
              : [0.299, 0.114];
        const green = 1 - red - blue;
        const lumaScale = frame.fullRange ? 1 : 255 / 219;
        const chromaScale = frame.fullRange ? 1 : 255 / 224;
        gl.uniform3f(offset, frame.fullRange ? 0 : 16 / 255, 128 / 255, 128 / 255);
        gl.uniformMatrix3fv(
          conversion,
          false,
          new Float32Array([
            lumaScale,
            lumaScale,
            lumaScale,
            0,
            ((-2 * blue * (1 - blue)) / green) * chromaScale,
            2 * (1 - blue) * chromaScale,
            2 * (1 - red) * chromaScale,
            ((-2 * red * (1 - red)) / green) * chromaScale,
            0,
          ]),
        );
      }
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    },
    dispose() {
      for (const texture of textures) gl.deleteTexture(texture);
      gl.deleteBuffer(vertices);
      gl.deleteProgram(program);
      gl.deleteShader(vertex);
      gl.deleteShader(fragment);
    },
  };
}
